import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, ChevronDown, Plus, Minus } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { Logo } from './Logo';
import { SOLUTION_CATEGORIES, SECTOR_GROUPS } from '../data/homeMenus';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [expandedMobileCategory, setExpandedMobileCategory] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const toggleMobileCategory = (category) => {
    setExpandedMobileCategory(expandedMobileCategory === category ? null : category);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Already Clickable */}
          <Logo />

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {/* About Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center text-slate-700 hover:text-emerald-600 transition-colors font-medium">
                About <ChevronDown className="ml-1 w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem asChild>
                  <a href="/about-us#mission" className="cursor-pointer">Our Mission</a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="/about-us#vision" className="cursor-pointer">Our Vision</a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="/about-us#values" className="cursor-pointer">Our Values</a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Capital Solutions Dropdown - Updated Name */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center text-slate-700 hover:text-emerald-600 transition-colors font-medium">
                Capital Solutions <ChevronDown className="ml-1 w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-80 max-h-[80vh] overflow-y-auto">
                
                {/* 1. Home & Property Loan */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <span className="font-semibold text-emerald-700">Home & Property Loan</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-72">
                    <DropdownMenuItem asChild><Link to="/home-loan">Home Loan</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/home-loan-overdraft">Home Loan Overdraft</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/loan-against-property">Loan Against Property</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/loan-against-property-overdraft">Loan Against Property Overdraft</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/commercial-property-purchase">Commercial Property Purchase Loan</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/home-loans-nri">Home Loans for NRI</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/loan-against-property-nri">Loan Against Property for NRI</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/builder-finance">Builder Finance</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/real-estate-funding">Real Estate Funding</Link></DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSeparator />

                {/* 2. Business & Corporate Finance */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <span className="font-semibold text-emerald-700">Business & Corporate Finance</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-72">
                    <DropdownMenuItem asChild><Link to="/business-loans">Business Loans</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/msme-business-funding">MSME Business Funding</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/working-capital-finance">Working Capital Finance</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/unsecured-working-capital">Unsecured Working Capital</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/term-loan">Term Loan</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/debt-syndication">Debt Syndication</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/project-finance">Project Finance</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/infrastructure-funding">Infrastructure Funding</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/solar-project-funding">Solar Project Funding</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/funding-for-schools">Funding for Schools</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/foreign-currency-funding">Foreign Currency Funding</Link></DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSeparator />

                {/* 3. Trade & Supply Chain Finance */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <span className="font-semibold text-emerald-700">Trade & Supply Chain Finance</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-72">
                    <DropdownMenuItem asChild><Link to="/trade-finance-solutions">Trade Finance Solutions</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/supply-chain-finance">Supply Chain Finance</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/vendor-financing">Vendor Financing</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/bill-discounting">Bill Discounting</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/factoring-finance">Factoring Finance</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/lc-discounting">LC Discounting</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/bank-guarantee">Bank Guarantee</Link></DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSeparator />

                {/* 4. Structured Credit & Capital Markets */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <span className="font-semibold text-emerald-700">Structured Credit & Capital Markets</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-72">
                    <DropdownMenuItem asChild><Link to="/private-equity-funding">Private Equity Funding</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/non-convertible-debentures">Non-Convertible Debentures (NCD)</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/securitization">Securitization</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/merger-acquisition-advisory">Merger & Acquisition Advisory</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/debt-advisory">Debt Advisory</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/capital-structuring">Capital Structuring</Link></DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSeparator />

                {/* 5. Asset-Backed Lending */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <span className="font-semibold text-emerald-700">Asset-Backed Lending</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-72">
                    <DropdownMenuItem asChild><Link to="/loan-against-shares">Loan Against Shares</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/loan-against-mutual-funds">Loan Against Mutual Funds</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/loan-against-fixed-deposits">Loan Against Fixed Deposits</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/loan-against-gold-silver">Loan Against Gold & Silver</Link></DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSeparator />

                {/* 6. Equipment & Machinery Finance */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <span className="font-semibold text-emerald-700">Equipment & Machinery Finance</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-72">
                    <DropdownMenuItem asChild><Link to="/machinery-loan">Machinery Loan</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/machinery-refinance">Machinery Refinance</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/imported-machinery-finance">Imported Machinery Finance</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/medical-equipment-finance">Medical Equipment Finance</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/equipment-leasing">Equipment Leasing</Link></DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSeparator />

                {/* 7. Professional & Education Finance */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <span className="font-semibold text-emerald-700">Professional & Education Finance</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-72">
                    <DropdownMenuItem asChild><Link to="/doctors-loan">Doctors Loan</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/chartered-accountant-loan">Chartered Accountant Loan</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/company-secretary-loan">Company Secretary Loan</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/architect-loan">Architect Loan</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/lawyer-loan">Lawyer Loan</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/education-loan">Education Loan</Link></DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSeparator />

                {/* 8. Specialized Funding Solutions */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <span className="font-semibold text-emerald-700">Specialized Funding Solutions</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-72">
                    <DropdownMenuItem asChild><Link to="/cgtmse-loan">CGTMSE Loan</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/project-report-funding">Project Report Funding</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/personal-loan-salaried">Personal Loan for Salaried</Link></DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

              </DropdownMenuContent>
            </DropdownMenu>

            {/* Business Sectors Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center text-slate-700 hover:text-emerald-600 transition-colors font-medium">
                Business Sectors <ChevronDown className="ml-1 w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72">
                <DropdownMenuItem asChild>
                  <Link to="/industries" className="font-semibold text-emerald-700" data-testid="nav-all-industries">View All Industries →</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                
                {/* Manufacturing & Industrial */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <span className="font-semibold text-emerald-700 flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Manufacturing & Industrial
                    </span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-64">
                    <DropdownMenuItem asChild><Link to="/industries/engineering">Engineering</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/auto-components">Auto Components</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/chemicals">Chemicals</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/metals">Metals</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/textiles">Textiles</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/packaging">Packaging</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/food-processing">Food Processing</Link></DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSeparator />

                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <span className="font-semibold text-emerald-700 flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Real Estate & Infrastructure
                    </span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-64">
                    <DropdownMenuItem asChild><Link to="/industries/developers">Developers</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/contractors">Contractors</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/epc-companies">EPC Companies</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/infrastructure">Infrastructure</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/commercial-real-estate">Commercial Real Estate</Link></DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSeparator />

                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <span className="font-semibold text-emerald-700 flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Healthcare & Life Sciences
                    </span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-64">
                    <DropdownMenuItem asChild><Link to="/industries/hospitals">Hospitals</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/doctors">Doctors</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/diagnostic-centres">Diagnostic Centres</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/pharmaceuticals">Pharmaceuticals</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/medical-equipment">Medical Equipment</Link></DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSeparator />

                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <span className="font-semibold text-emerald-700 flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Technology & Services
                    </span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-64">
                    <DropdownMenuItem asChild><Link to="/industries/it-companies">IT Companies</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/saas-businesses">SaaS Businesses</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/consulting-firms">Consulting Firms</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/professional-services">Professional Services</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/media-entertainment">Media & Entertainment</Link></DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSeparator />

                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <span className="font-semibold text-emerald-700 flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Trade & Commerce
                    </span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-64">
                    <DropdownMenuItem asChild><Link to="/industries/import-export">Import & Export</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/wholesale">Wholesale</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/retail">Retail</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/ecommerce">E-commerce</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/logistics">Logistics</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/supply-chain">Supply Chain</Link></DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSeparator />

                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <span className="font-semibold text-emerald-700 flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Education & Institutions
                    </span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-64">
                    <DropdownMenuItem asChild><Link to="/industries/schools">Schools</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/colleges">Colleges</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/universities">Universities</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/industries/educational-trusts">Educational Trusts</Link></DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

              </DropdownMenuContent>
            </DropdownMenu>

            {/* Calculator Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center text-slate-700 hover:text-emerald-600 transition-colors font-medium">
                Calculator <ChevronDown className="ml-1 w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72">
                <DropdownMenuItem asChild>
                  <Link to="/calculators/emi" className="font-medium text-emerald-700">EMI Calculator</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/calculators/eligibility" className="font-medium text-emerald-700">Eligibility Calculator</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/calculators/overdraft" className="font-medium text-emerald-700">Overdraft Calculator</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/calculators/home-loan-overdraft" className="font-medium text-emerald-700">Home Loan Overdraft Calculator</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/calculators/balance-transfer" className="font-medium text-emerald-700">Home Loan Balance Transfer Calculator</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/calculators/buy-vs-rent" className="font-medium text-emerald-700">Buy Vs Rent Calculator</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/calculators/prepayment" className="font-medium text-emerald-700">Loan Prepayment Calculator</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/become-partner" className="text-slate-700 hover:text-emerald-600 transition-colors font-medium">
              Become Partner
            </Link>
          </nav>

          {/* CTA Button - Desktop - Compact */}
          <div className="hidden lg:block">
            <Button 
              asChild
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-4 py-2 h-auto flex items-center gap-1.5 text-sm"
            >
              <a href="/#contact" data-testid="header-contact-link">
                <Phone className="w-3.5 h-3.5" />
                <span className="whitespace-nowrap">Contact</span>
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden relative z-50 p-2 text-slate-700 hover:text-emerald-600"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-20 left-0 right-0 bg-white border-t border-slate-200 shadow-xl max-h-[calc(100vh-5rem)] overflow-y-auto">
          <nav className="container mx-auto px-4 py-6 space-y-4">
            {/* About Section */}
            <div>
              <button
                onClick={() => toggleSection('about')}
                className="flex items-center justify-between w-full py-3 text-slate-700 font-semibold"
              >
                <span>About</span>
                {expandedSection === 'about' ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </button>
              {expandedSection === 'about' && (
                <div className="pl-4 space-y-2 mt-2">
                  <a href="/about-us#mission" className="block py-2 text-slate-600" onClick={() => setIsMobileMenuOpen(false)}>Our Mission</a>
                  <a href="/about-us#vision" className="block py-2 text-slate-600" onClick={() => setIsMobileMenuOpen(false)}>Our Vision</a>
                  <a href="/about-us#values" className="block py-2 text-slate-600" onClick={() => setIsMobileMenuOpen(false)}>Our Values</a>
                </div>
              )}
            </div>

            {/* Debt & Capital Solutions Section with Expandable Categories */}
            <div>
              <button
                onClick={() => toggleSection('solutions')}
                className="flex items-center justify-between w-full py-3 text-slate-700 font-semibold"
              >
                <span>Capital Solutions</span>
                {expandedSection === 'solutions' ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </button>
              {expandedSection === 'solutions' && (
                <div className="pl-4 space-y-3 mt-2">
                  {SOLUTION_CATEGORIES.map((cat) => (
                    <div key={cat.slug}>
                      <button
                        onClick={() => toggleMobileCategory(cat.slug)}
                        aria-expanded={expandedMobileCategory === cat.slug}
                        data-testid={`mobile-cat-${cat.slug}`}
                        className="flex items-center justify-between w-full py-2"
                      >
                        <span className="font-semibold text-emerald-700 text-sm">{cat.name}</span>
                        {expandedMobileCategory === cat.slug ? <Minus className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                      </button>
                      {expandedMobileCategory === cat.slug && (
                        <div className="pl-3 mt-1">
                          <Link
                            to={`/solutions/${cat.slug}`}
                            className="block py-2 text-sm font-semibold text-emerald-700"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            All {cat.name} →
                          </Link>
                          {cat.items.map(([label, to]) => (
                            <Link
                              key={to}
                              to={to}
                              className="block py-2 text-sm text-slate-600"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Industries Section */}
            <div>
              <button
                onClick={() => toggleSection('industries')}
                className="flex items-center justify-between w-full py-3 text-slate-700 font-semibold"
              >
                <span>Business Sectors</span>
                {expandedSection === 'industries' ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </button>
              {expandedSection === 'industries' && (
                <div className="pl-4 space-y-1 mt-2 text-sm">
                  {SECTOR_GROUPS.map((group) => (
                    <div key={group.name}>
                      <p className="font-semibold text-emerald-700 mb-1 mt-3">{group.name}</p>
                      {group.items.map(([label, to]) => (
                        <Link
                          key={to}
                          to={to}
                          className="block py-2 text-slate-600"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {label}
                        </Link>
                      ))}
                    </div>
                  ))}
                  <Link to="/industries" className="block py-2 mt-2 font-semibold text-emerald-700" onClick={() => setIsMobileMenuOpen(false)}>
                    View All Industries →
                  </Link>
                </div>
              )}
            </div>

            {/* Calculator Section */}
            <div>
              <button
                onClick={() => toggleSection('calculators')}
                className="flex items-center justify-between w-full py-3 text-slate-700 font-semibold"
              >
                <span>Calculator</span>
                {expandedSection === 'calculators' ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </button>
              {expandedSection === 'calculators' && (
                <div className="pl-4 space-y-3 mt-2 text-sm">
                  <Link to="/calculators/emi" className="block py-2 text-slate-700" onClick={() => setIsMobileMenuOpen(false)}>
                    EMI Calculator
                  </Link>
                  <Link to="/calculators/eligibility" className="block py-2 text-slate-700" onClick={() => setIsMobileMenuOpen(false)}>
                    Eligibility Calculator
                  </Link>
                  <Link to="/calculators/overdraft" className="block py-2 text-slate-700" onClick={() => setIsMobileMenuOpen(false)}>
                    Overdraft Calculator
                  </Link>
                  <Link to="/calculators/home-loan-overdraft" className="block py-2 text-slate-700" onClick={() => setIsMobileMenuOpen(false)}>
                    Home Loan Overdraft Calculator
                  </Link>
                  <Link to="/calculators/balance-transfer" className="block py-2 text-slate-700" onClick={() => setIsMobileMenuOpen(false)}>
                    Home Loan Balance Transfer Calculator
                  </Link>
                  <Link to="/calculators/buy-vs-rent" className="block py-2 text-slate-700" onClick={() => setIsMobileMenuOpen(false)}>
                    Buy Vs Rent Calculator
                  </Link>
                  <Link to="/calculators/prepayment" className="block py-2 text-slate-700" onClick={() => setIsMobileMenuOpen(false)}>
                    Loan Prepayment Calculator
                  </Link>
                </div>
              )}
            </div>

            <Link to="/become-partner" className="block py-3 text-slate-700 font-semibold" onClick={() => setIsMobileMenuOpen(false)}>
              Become Partner
            </Link>

            <Button 
              asChild
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium mt-4"
            >
              <a href="/#contact" data-testid="mobile-contact-link" onClick={() => setIsMobileMenuOpen(false)}>
                <Phone className="w-4 h-4 mr-2" />
                Contact
              </a>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};
